<template>
  <div>
    <div class="row" v-if="!isHomeDashboard">
      <div class="col-12">
        <DashboardContentBox>
          <div class="heading-box">
            <img src="glass/img/dashboard-header-image.png" class="heading-image" v-if="!hasCompanyLogo" />
            <img :src="accountLogoURL" class="heading-image" v-if="hasCompanyLogo" />

            <div class="dashboard-header">
              <ReportFilterPopover
                headerTitle="Analytics Location Filter"
                :showApplyButton="true"
                :showResetButton="true"
                :appendToBody="true"
                class="float-right"
                alignment="right"
                placement="bottom-end"
                @apply="applyDashboardFilter"
                @reset="resetDashboardFilter">
                <template v-slot:popover>
                  <div class="row">
                    <div class="col-12 grid-force-newlines">
                      <FormRow label="Account Locations">
                        <FormMultiSelect
                          :options="accountLocationLookup"
                          v-model="editingDashboardFilter.accountLocationIdList" />
                      </FormRow>
                      <small><i class="far fa-info-circle" /> Note: Not all analytics reports can be filtered by account locations and will just display the account wide data</small>
                    </div>
                  </div>                                    
                </template>
                <template v-slot:filterDisplay>
                  <div v-if="hasFiltersSelected">
                    <i class="far fa-map-marker-alt mr-2"></i> {{filteredLocationListDisplay}}
                  </div>                
                </template>
              </ReportFilterPopover>

              <h2>{{profileCompanyName}}</h2>
              <div class="pt-4 pb-1">
                <TabPillButton tabid="tab-overview" v-model="currentTab">Overview</TabPillButton>
                <TabPillButton tabid="tab-contact" v-model="currentTab" v-if="userHasReadPermission(PermissionTypes.IntelligencePermissionTypes.intelligenceSalesPricing)">Contacts</TabPillButton>
                <TabPillButton tabid="tab-inventory" v-model="currentTab" v-if="userHasReadPermission(PermissionTypes.IntelligencePermissionTypes.intelligenceInventory)">Inventory</TabPillButton>
                <TabPillButton tabid="tab-purchasing" v-model="currentTab" v-if="userHasReadPermission(PermissionTypes.IntelligencePermissionTypes.intelligenceInventory)">Purchasing</TabPillButton>
                <TabPillButton tabid="tab-orders" v-model="currentTab" v-if="userHasReadPermission(PermissionTypes.IntelligencePermissionTypes.intelligenceOrder)">Orders</TabPillButton>
                <TabPillButton tabid="tab-quotes" v-model="currentTab" v-if="userHasReadPermission(PermissionTypes.IntelligencePermissionTypes.intelligenceOrder)">Quotes</TabPillButton>
                <TabPillButton tabid="tab-ncr" v-model="currentTab" v-if="userHasReadPermission(PermissionTypes.IntelligencePermissionTypes.intelligenceOrder)">NCRs</TabPillButton>
                <TabPillButton tabid="tab-production" v-model="currentTab" v-if="userHasReadPermission(PermissionTypes.IntelligencePermissionTypes.intelligenceProduction)">Production</TabPillButton>
                <TabPillButton tabid="tab-optimiser" v-model="currentTab" v-if="userHasReadPermission(PermissionTypes.IntelligencePermissionTypes.intelligenceProduction)">Optimiser</TabPillButton>
                <TabPillButton tabid="tab-recuts" v-model="currentTab" v-if="userHasReadPermission(PermissionTypes.IntelligencePermissionTypes.intelligenceProduction)">Recuts</TabPillButton>
                <TabPillButton tabid="tab-task" v-model="currentTab" v-if="userHasReadPermission(PermissionTypes.IntelligencePermissionTypes.intelligenceProduction)">Tasks</TabPillButton>
                <TabPillButton tabid="tab-difot" v-model="currentTab" v-if="userHasReadPermission(PermissionTypes.IntelligencePermissionTypes.intelligenceDIFOT)">DIFOT</TabPillButton>
                <TabPillButton tabid="tab-accounts" v-model="currentTab" v-if="userHasReadPermission(PermissionTypes.IntelligencePermissionTypes.intelligenceAccount)">Accounts</TabPillButton>
              </div>
            </div>            
          </div>
        </DashboardContentBox>
      </div>
    </div>

    <TabPillContent tabid="tab-overview" :currentTab="currentTab" v-if="!isHomeDashboard">
      <AnalyticsOverviewDashboard :filter="appliedDashboardFilter" />
    </TabPillContent>

    <TabPillContent tabid="tab-contact" :currentTab="currentTab" v-if="!isHomeDashboard">
      <AnalyticsContactDashboard :filter="appliedDashboardFilter" />
    </TabPillContent>

    <TabPillContent tabid="tab-inventory" :currentTab="currentTab" v-if="!isHomeDashboard">
      <AnalyticsInventoryDashboard :filter="appliedDashboardFilter" />
    </TabPillContent>

    <TabPillContent tabid="tab-purchasing" :currentTab="currentTab" v-if="!isHomeDashboard">
      <AnalyticsPurchasingDashboard :filter="appliedDashboardFilter" />
    </TabPillContent>

    <TabPillContent tabid="tab-orders" :currentTab="currentTab" v-if="!isHomeDashboard">
      <AnalyticsOrderDashboard :filter="appliedDashboardFilter" />
    </TabPillContent>

    <TabPillContent tabid="tab-quotes" :currentTab="currentTab" v-if="!isHomeDashboard">
      <AnalyticsQuotesDashboard :filter="appliedDashboardFilter" />
    </TabPillContent>

    <TabPillContent tabid="tab-ncr" :currentTab="currentTab" v-if="!isHomeDashboard">
      <AnalyticsNCRDashboard :filter="appliedDashboardFilter" />
    </TabPillContent>

    <TabPillContent tabid="tab-production" :currentTab="currentTab" v-if="!isHomeDashboard">
      <AnalyticsProductionDashboard :filter="appliedDashboardFilter" />
    </TabPillContent>

    <TabPillContent tabid="tab-optimiser" :currentTab="currentTab" v-if="!isHomeDashboard">
      <AnalyticsOptimiserDashboard :filter="appliedDashboardFilter" />
    </TabPillContent>

    <TabPillContent tabid="tab-recuts" :currentTab="currentTab" v-if="!isHomeDashboard">
      <AnalyticsRecutDashboard :filter="appliedDashboardFilter" />
    </TabPillContent>

    <TabPillContent tabid="tab-task" :currentTab="currentTab" v-if="!isHomeDashboard">
      <AnalyticsTasksDashboard :filter="appliedDashboardFilter" />
    </TabPillContent>

    <TabPillContent tabid="tab-difot" :currentTab="currentTab" v-if="!isHomeDashboard">
      <AnalyticsDIFOTDashboard :filter="appliedDashboardFilter" />
    </TabPillContent>

    <TabPillContent tabid="tab-accounts" :currentTab="currentTab" v-if="!isHomeDashboard">
      <AnalyticsAccountsDashboard :filter="appliedDashboardFilter" />
    </TabPillContent>

    <!--
    <div class="row" v-if="isHomeDashboard">
      <div class="col-12">
        <DashboardContentBox>
          <div class="heading-box">
            <img src="glass/img/dashboard-header-image.png" class="heading-image" />
            <div class="dashboard-header">
              <h2>{{profileCompanyName}}</h2>
            </div>            
          </div>
        </DashboardContentBox>
      </div>
    </div>
    -->


    <div class="row" v-if="isHomeDashboard">
      <div class="col-12 col-lg-5">
        <DashboardGreetingBox />
      </div>
      <div class="col-7 d-none d-lg-block">
        <DashboardAnalyticsBox /> 
      </div>
    </div>


  </div>
</template>

<style lang="scss" scoped>
.heading-box {
  position: relative;
  min-height: 60px;
  overflow: hidden;
  padding: 6px;
}
.heading-image {
  position: absolute;
  top: 0px;
  left: 0px;
  border-top-left-radius: 6px;
  width: 80px;
}
.heading-edit-dropdown {
  position: absolute;
  top: 10px;
  right: 10px;
}
.dashboard-header {
  padding: 0.5rem;
}
.dashboard-header > h2 {
  color: $general-highlight-color;
  font-weight: 600;
  margin-left: 80px;
}
.analytics-report-group {
  padding: 0.5em;
}
</style>


<script>
import _ from 'lodash'
import { mapState } from "vuex"

import DashboardContentBox from '@/components/dashboard/dashboardContentBox'
import DashboardTabbedContentBox from '@/components/dashboard/dashboardTabbedContentBox'

import TabPillButton from '@/components/layout/tabPillButton.vue'
import TabPillContent from '@/components/layout/tabPillContent.vue'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import AnalyticsReportLink from '@/modules/intelligence/components/analyticsReportLink'

// Testing Widgets
import DashboardGreetingBox from '@/modules/core/components/dashboardGreetingBox'
import DashboardAnalyticsBox from '@/modules/core/components/dashboardAnalyticsBox'
import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'

// Module Dashboards
import AnalyticsOverviewDashboard from '@/modules/intelligence/reportViews/analyticsOverviewDashboard'
import AnalyticsContactDashboard from '@/modules/intelligence/reportViews/contacts/analyticsContactDashboard'
import AnalyticsInventoryDashboard from '@/modules/intelligence/reportViews/inventory/analyticsInventoryDashboard'
import AnalyticsPurchasingDashboard from '@/modules/intelligence/reportViews/purchasing/analyticsPurchasingDashboard'
import AnalyticsOrderDashboard from '@/modules/intelligence/reportViews/orders/analyticsOrderDashboard'
import AnalyticsQuotesDashboard from '@/modules/intelligence/reportViews/orders/analyticsQuotesDashboard'
import AnalyticsNCRDashboard from '@/modules/intelligence/reportViews/orders/analyticsNCRDashboard'
import AnalyticsProductionDashboard from '@/modules/intelligence/reportViews/production/analyticsProductionDashboard'
import AnalyticsRecutDashboard from '@/modules/intelligence/reportViews/production/analyticsRecutDashboard'
import AnalyticsOptimiserDashboard from '@/modules/intelligence/reportViews/optimiser/analyticsOptimiserDashboard'
import AnalyticsTasksDashboard from '@/modules/intelligence/reportViews/tasks/analyticsTasksDashboard'
import AnalyticsDIFOTDashboard from '@/modules/intelligence/reportViews/difot/analyticsDIFOTDashboard'
import AnalyticsAccountsDashboard from '@/modules/intelligence/reportViews/accounts/analyticsAccountsDashboard'

export default {
  name: 'AnalyticsDashboard',
  components: {
    DashboardContentBox,
    DashboardTabbedContentBox,
    TabPillButton,
    TabPillContent,
    ReportFilterPopover,
    AnalyticsReportLink,
    DashboardGreetingBox,
    DashboardAnalyticsBox,
    AnalyticsWidget,

    AnalyticsOverviewDashboard,
    AnalyticsContactDashboard,
    AnalyticsInventoryDashboard,
    AnalyticsPurchasingDashboard,
    AnalyticsOrderDashboard,
    AnalyticsQuotesDashboard,
    AnalyticsNCRDashboard,
    AnalyticsProductionDashboard,
    AnalyticsRecutDashboard,
    AnalyticsOptimiserDashboard,
    AnalyticsTasksDashboard,
    AnalyticsDIFOTDashboard,
    AnalyticsAccountsDashboard
  },
  props: {
    type: String
  },
  computed: {
    ...mapState({
      accountLocations: state => state.account.accountLocations
    }),
    hasCompanyLogo() {
      return !!this.accountLogoURL
    },
    accountLogoURL() {
      return this.$store.state.profile.profileLogoURL
    },
    profileCompanyName() {
      return this.$store.state.profile.profileCompanyName
    },
    profileName() {
      return this.$store.state.profile.profileUser.name
    },
    isHomeDashboard() {
      return this.type == 'home'
    },
    accountLocationLookup() {
      return (this.accountLocations || []).filter(location => location.isActive).map(location => {
        return {
          name: location.name,
          value: location.id
        }
      })
    },
    filteredLocationListDisplay() {
      return this.formatMultiSelectLookupName(this.appliedDashboardFilter.accountLocationIdList, this.accountLocationLookup)
    },
    hasFiltersSelected() {
      return this.appliedDashboardFilter.accountLocationIdList && this.appliedDashboardFilter.accountLocationIdList.length > 0    
    }
  },
  data() {
    return {
      currentTab: 'tab-overview',
      defaultDashboardFilter: {
        accountLocationIdList: []
      },
      editingDashboardFilter: {},
      appliedDashboardFilter: {
        accountLocationIdList: []
      }
    }
  },
  created() {
  },
  mounted() {
    this.editingDashboardFilter = _.clone(this.defaultDashboardFilter)
    this.appliedDashboardFilter = _.clone(this.defaultDashboardFilter)

    if(this.$route.hash) {
      var hashTabId = (this.$route.hash + '').replace('#', '')
      if(hashTabId)
        this.currentTab = hashTabId
    }
  },
  beforeDestroy() {
  },
  methods: {
    applyDashboardFilter() {
      this.appliedDashboardFilter = _.clone(this.editingDashboardFilter)
    },
    resetDashboardFilter() {
      this.editingDashboardFilter = _.clone(this.defaultDashboardFilter)
      this.appliedDashboardFilter = _.clone(this.defaultDashboardFilter)
    },
  }
}
</script>